import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Stack, Tooltip, IconButton, Autocomplete
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSnackbar } from 'notistack';
import PageHeader from '../components/PageHeader';
import PermissionGate from '../components/PermissionGate';
import ConfirmDialog from '../components/ConfirmDialog';
import api from '../api';

const emptyForm = { name: '', displayName: '', description: '', permissions: [] };

// Product-owned roles + the permissions they grant. Composing roles here is how the simulator builds
// the product permissions it later publishes to the gateway (User Roles page → permission-sync).
const RolesManager = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [roles, setRoles] = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([api.get('/roles'), api.get('/permissions')]);
      setRoles(r.data.roles);
      setAllPerms(p.data.permissions);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to load roles', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setFormOpen(true); };
  const openEdit = (row) => {
    setEditing(row.id);
    setForm({
      name: row.name,
      displayName: row.display_name,
      description: row.description || '',
      permissions: row.permissions || []
    });
    setFormOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      let roleId = editing;
      if (editing) {
        await api.put(`/roles/${editing}`, { displayName: form.displayName, description: form.description });
      } else {
        const { data } = await api.post('/roles', {
          name: form.name, displayName: form.displayName, description: form.description
        });
        roleId = data.role.id;
      }
      const { data: permRes } = await api.put(`/roles/${roleId}/permissions`, {
        permissionIds: form.permissions.map((p) => p.id)
      });
      const n = permRes?.republished ?? 0;
      enqueueSnackbar(
        `${editing ? 'Role updated' : 'Role created'}` +
          (n ? ` — re-published permissions for ${n} user${n === 1 ? '' : 's'}` : ''),
        { variant: 'success' }
      );
      if (permRes?.republishFailed) {
        enqueueSnackbar(`${permRes.republishFailed} user(s) failed to re-publish — check server logs`, { variant: 'warning' });
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Save failed', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/roles/${deleteTarget.id}`);
      enqueueSnackbar('Role deleted', { variant: 'success' });
      setDeleteTarget(null);
      await load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Delete failed', { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { field: 'name', headerName: 'Name', width: 150 },
    { field: 'display_name', headerName: 'Display name', width: 170 },
    {
      field: 'permissions', headerName: 'Permissions', flex: 1, minWidth: 240, sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ py: 0.5 }}>
          {(p.value || []).length
            ? p.value.map((perm) => (
                <Chip key={perm.id} label={perm.name} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
              ))
            : <Chip label="none" size="small" variant="outlined" color="default" />}
        </Stack>
      )
    },
    {
      field: 'actions', headerName: 'Actions', width: 110, sortable: false, filterable: false,
      renderCell: (p) => (
        <Stack direction="row">
          <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(p.row)}><EditIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteTarget(p.row)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
        </Stack>
      )
    }
  ];

  return (
    <Box>
      <PageHeader
        title="Roles"
        subtitle="Product-owned roles and the permissions they grant. Assign roles to users on the User Roles page."
        action={
          <Stack direction="row" spacing={1}>
            <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New role</Button>
          </Stack>
        }
      />

      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 2 }}>
        <DataGrid
          rows={roles}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10, 25, 50]}
        />
      </Box>

      <Dialog open={formOpen} onClose={saving ? undefined : () => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit role' : 'New role'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name" fullWidth value={form.name} disabled={!!editing}
              helperText={editing ? 'Name is immutable' : 'e.g. finance_manager'}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <TextField
              label="Display name" fullWidth value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            />
            <TextField
              label="Description" fullWidth multiline minRows={2} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <Autocomplete
              multiple options={allPerms} value={form.permissions}
              getOptionLabel={(o) => `${o.module}:${o.name}`}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              groupBy={(o) => o.module}
              onChange={(_e, val) => setForm({ ...form, permissions: val })}
              renderInput={(params) => <TextField {...params} label="Permissions" placeholder="Assign permissions" />}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || !form.name || !form.displayName}>
            {editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete role?"
        message={`Delete role "${deleteTarget?.display_name}"? This removes its permission mappings and any user assignments.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleting}
        onConfirm={remove}
        onClose={() => setDeleteTarget(null)}
      />
    </Box>
  );
};

const Roles = () => (
  <PermissionGate permission="users:manage">
    <RolesManager />
  </PermissionGate>
);

export default Roles;
